import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, BACKEND_URL } from "../../App";
import { descargarArchivo } from "../../utils/download";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { 
  Plus, Search, UserCog, Edit, Trash2, Phone, MessageCircle, Briefcase, Clock, Download
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";

const roles = [
  { value: "mesero_salon", label: "Mesero Salón" },
  { value: "mesero_terraza", label: "Mesero Terraza" },
  { value: "barra", label: "Barra" },
  { value: "limpieza", label: "Limpieza" },
  { value: "cocina", label: "Cocina" },
  { value: "decorador", label: "Decorador" },
  { value: "planner", label: "Planner" },
  { value: "chef", label: "Chef" },
  { value: "dj", label: "DJ" },
  { value: "fotografo", label: "Fotógrafo" },
  { value: "otro", label: "Otro" },
];

const tiposContrato = [
  { value: "eventual", label: "Eventual" },
  { value: "planta", label: "Planta" },
  { value: "freelance", label: "Freelance" },
];

const disponibilidades = [
  { value: "disponible", label: "Disponible", color: "bg-emerald-100 text-emerald-800" },
  { value: "no_disponible", label: "No Disponible", color: "bg-red-100 text-red-800" },
  { value: "vacaciones", label: "Vacaciones", color: "bg-amber-100 text-amber-800" },
];

// Iter132 · Esquemas de pago del Personal
const esquemasPago = [
  { value: "no_definido", label: "Sin definir", sufijo: "" },
  { value: "por_hora", label: "Por hora", sufijo: "/hora" },
  { value: "por_evento", label: "Por evento", sufijo: "/evento" },
  { value: "por_jornada", label: "Por jornada", sufijo: "/jornada" },
  { value: "por_servicio", label: "Por servicio", sufijo: "/servicio" },
  { value: "fijo", label: "Fijo", sufijo: " fijo" },
  { value: "personalizado", label: "Personalizado", sufijo: "" },
];
const esquemaMeta = (v) => esquemasPago.find(x => x.value === v) || esquemasPago[0];

const Personal = () => {
  const [personal, setPersonal] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterRol, setFilterRol] = useState("todos");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPersonal, setEditingPersonal] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [formData, setFormData] = useState({
    nombre: "", rol: "", telefono: "", whatsapp: "",
    pago_hora: "", tipo_contrato: "eventual",
    disponibilidad: "disponible", notas: "",
    esquema_pago: "no_definido", pago_monto: "", pago_concepto: "",
  });
  // Iter132 · Rol y Tipo de contrato admiten valor libre además del catálogo.
  const [rolPersonalizado, setRolPersonalizado] = useState(false);
  const [contratoPersonalizado, setContratoPersonalizado] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/personal`);
      setPersonal(response.data);
    } catch {
      toast.error("Error al cargar personal");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    if (!formData.nombre || !formData.rol) {
      toast.error("Nombre y rol son requeridos");
      return;
    }
    try {
      const monto = formData.pago_monto !== "" ? parseFloat(formData.pago_monto) : null;
      const esquema = formData.esquema_pago || "no_definido";
      const dataToSend = {
        ...formData,
        esquema_pago: esquema,
        pago_monto: Number.isFinite(monto) ? monto : null,
        pago_concepto: esquema === "personalizado" ? (formData.pago_concepto || "") : "",
        // Compatibilidad: solo se mantiene pago_hora cuando el esquema es Por hora.
        // Para otros esquemas se limpia para no confundir a módulos que aún lo lean.
        pago_hora: esquema === "por_hora" && Number.isFinite(monto) ? monto : null,
      };
      if (editingPersonal) {
        await axios.put(`${API}/personal/${editingPersonal.personal_id}`, dataToSend);
        toast.success("Personal actualizado");
      } else {
        await axios.post(`${API}/personal`, dataToSend);
        toast.success("Personal agregado");
      }
      resetForm();
      await fetchData();
      setDialogOpen(false);
    } catch (error) {
      toast.error("Error al guardar");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/personal/${deleteId}`);
      toast.success("Personal eliminado");
      await fetchData();
    } catch (error) {
      toast.error("Error al eliminar");
    } finally {
      setDeleteId(null);
    }
  };

  const openEditDialog = (p) => {
    setEditingPersonal(p);
    // Deriva esquema/monto conservando datos existentes: si venía solo con pago_hora, se
    // marca como "Por hora" automáticamente para no perder el importe capturado.
    const esquema = p.esquema_pago || (p.pago_hora != null && p.pago_hora !== "" ? "por_hora" : "no_definido");
    const monto = (p.pago_monto != null && p.pago_monto !== "")
      ? p.pago_monto
      : (esquema === "por_hora" && p.pago_hora != null ? p.pago_hora : "");
    setFormData({
      nombre: p.nombre, rol: p.rol, telefono: p.telefono || "",
      whatsapp: p.whatsapp || "", pago_hora: p.pago_hora?.toString() || "",
      tipo_contrato: p.tipo_contrato || "eventual",
      disponibilidad: p.disponibilidad || "disponible", notas: p.notas || "",
      esquema_pago: esquema,
      pago_monto: monto === null || monto === undefined ? "" : monto.toString(),
      pago_concepto: p.pago_concepto || "",
    });
    // Si el valor guardado no está en el catálogo, se abre en modo Personalizar.
    setRolPersonalizado(!!p.rol && !roles.some(r => r.value === p.rol));
    setContratoPersonalizado(!!p.tipo_contrato && !tiposContrato.some(t => t.value === p.tipo_contrato));
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingPersonal(null);
    setFormData({
      nombre: "", rol: "", telefono: "", whatsapp: "",
      pago_hora: "", tipo_contrato: "eventual",
      disponibilidad: "disponible", notas: "",
      esquema_pago: "no_definido", pago_monto: "", pago_concepto: "",
    });
    setRolPersonalizado(false);
    setContratoPersonalizado(false);
  };

  const getRolLabel = (rol) => roles.find(r => r.value === rol)?.label || rol;
  const getDisponibilidad = (d) => disponibilidades.find(x => x.value === d) || disponibilidades[0];
  const getContratoLabel = (t) => tiposContrato.find(x => x.value === t)?.label || t;

  const filteredPersonal = personal.filter(p => {
    const matchesSearch = p.nombre.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRol = filterRol === "todos" || p.rol === filterRol;
    return matchesSearch && matchesRol;
  });

  const disponibles = personal.filter(p => p.disponibilidad === "disponible").length;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl text-stone-900">Personal</h1>
          <p className="text-stone-600 mt-1">{personal.length} miembros | {disponibles} disponibles</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={async () => {
            toast.info("Generando PDF de personal...");
            try {
              const res = await axios.get(`${API}/admin/exportar/pdf/personal`);
              descargarArchivo(res.data.url, res.data.filename);
              toast.success("PDF descargado");
            } catch { toast.error("Error al generar PDF"); }
          }} className="border-stone-300" data-testid="btn-pdf-personal">
            <Download className="w-4 h-4 mr-2" />
            Descargar PDF
          </Button>
          <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="btn-primary" data-testid="btn-nuevo-personal">
            <Plus className="w-4 h-4 mr-2" />
            Agregar Personal
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4">
        {roles.slice(0, 4).map(rol => {
          const count = personal.filter(p => p.rol === rol.value).length;
          return (
            <Card key={rol.value} className="border-stone-200">
              <CardContent className="p-4">
                <p className="text-xs text-stone-500 uppercase tracking-wider">{rol.label}</p>
                <p className="font-display text-2xl text-stone-900">{count}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
          <Input placeholder="Buscar personal..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 border-stone-300" data-testid="search-personal" />
        </div>
        <Select value={filterRol} onValueChange={setFilterRol}>
          <SelectTrigger className="w-full md:w-48 border-stone-300" data-testid="filter-rol">
            <SelectValue placeholder="Filtrar por rol" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los roles</SelectItem>
            {roles.map(rol => (
              <SelectItem key={rol.value} value={rol.value}>{rol.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : filteredPersonal.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPersonal.map(p => {
            const disp = getDisponibilidad(p.disponibilidad);
            return (
              <Card key={p.personal_id} className="border-stone-200 hover:shadow-lg transition-shadow" data-testid={`personal-card-${p.personal_id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                        <UserCog className="w-6 h-6 text-emerald-900" />
                      </div>
                      <div>
                        <p className="font-medium text-stone-900">{p.nombre}</p>
                        <span className="badge badge-design">{getRolLabel(p.rol)}</span>
                      </div>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${disp.color}`}>
                      {disp.label}
                    </span>
                  </div>

                  <div className="space-y-2 text-sm mb-4">
                    {p.telefono && (
                      <div className="flex items-center gap-2 text-stone-600">
                        <Phone className="w-4 h-4" />
                        {p.telefono}
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-stone-600">
                      <Briefcase className="w-4 h-4" />
                      <span>{getContratoLabel(p.tipo_contrato)}</span>
                    </div>
                    {(() => {
                      const esquema = p.esquema_pago || (p.pago_hora != null ? "por_hora" : null);
                      const monto = (p.pago_monto != null && p.pago_monto !== "") ? p.pago_monto : (esquema === "por_hora" ? p.pago_hora : null);
                      if (!esquema || esquema === "no_definido") return null;
                      const meta = esquemaMeta(esquema);
                      const etiqueta = esquema === "personalizado"
                        ? (p.pago_concepto || meta.label)
                        : `${meta.label}${meta.sufijo}`;
                      return (
                        <div className="flex items-center gap-2 text-stone-600">
                          <Clock className="w-4 h-4" />
                          <span>{monto != null ? `$${monto} · ${etiqueta}` : etiqueta}</span>
                        </div>
                      );
                    })()}
                  </div>

                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEditDialog(p)} className="border-stone-300 text-xs flex-1" data-testid={`btn-editar-${p.personal_id}`}>
                      <Edit className="w-3 h-3 mr-1" />
                      Editar
                    </Button>
                    {p.whatsapp && (
                      <a href={`https://wa.me/52${p.whatsapp}`} target="_blank" rel="noopener noreferrer">
                        <Button variant="outline" size="sm" className="border-stone-300">
                          <MessageCircle className="w-4 h-4" />
                        </Button>
                      </a>
                    )}
                    <Button size="sm" variant="outline" onClick={() => setDeleteId(p.personal_id)} className="border-red-300 text-red-600 hover:bg-red-50">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <div className="bg-white border border-stone-200 rounded-sm p-12 text-center">
          <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <UserCog className="w-8 h-8 text-stone-400" />
          </div>
          <h3 className="font-display text-xl text-stone-900 mb-2">No hay personal</h3>
          <p className="text-stone-600">Agrega miembros de tu equipo</p>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {editingPersonal ? "Editar Personal" : "Agregar Personal"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nombre *</Label>
              <Input value={formData.nombre} onChange={(e) => setFormData(prev => ({ ...prev, nombre: e.target.value }))} className="border-stone-300" data-testid="input-nombre-personal" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Rol *</Label>
                {rolPersonalizado ? (
                  <div className="flex gap-2">
                    <Input
                      value={formData.rol}
                      onChange={(e) => setFormData(prev => ({ ...prev, rol: e.target.value }))}
                      placeholder="Escribe el rol"
                      className="border-stone-300"
                      data-testid="input-rol-personalizado"
                      autoFocus
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="border-stone-300"
                      onClick={() => { setRolPersonalizado(false); setFormData(prev => ({ ...prev, rol: "" })); }}
                      data-testid="btn-rol-volver-lista"
                      title="Volver a la lista"
                    >×</Button>
                  </div>
                ) : (
                  <Select
                    value={formData.rol}
                    onValueChange={(v) => {
                      if (v === "__custom__") { setRolPersonalizado(true); setFormData(prev => ({ ...prev, rol: "" })); }
                      else setFormData(prev => ({ ...prev, rol: v }));
                    }}
                  >
                    <SelectTrigger className="border-stone-300" data-testid="select-rol-personal">
                      <SelectValue placeholder="Selecciona un rol" />
                    </SelectTrigger>
                    <SelectContent>
                      {roles.map(rol => (
                        <SelectItem key={rol.value} value={rol.value}>{rol.label}</SelectItem>
                      ))}
                      <SelectItem value="__custom__" data-testid="option-rol-personalizar">+ Personalizar</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div className="space-y-2">
                <Label>Tipo de contrato</Label>
                {contratoPersonalizado ? (
                  <div className="flex gap-2">
                    <Input
                      value={formData.tipo_contrato}
                      onChange={(e) => setFormData(prev => ({ ...prev, tipo_contrato: e.target.value }))}
                      placeholder="Escribe la modalidad"
                      className="border-stone-300"
                      data-testid="input-contrato-personalizado"
                      autoFocus
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="border-stone-300"
                      onClick={() => { setContratoPersonalizado(false); setFormData(prev => ({ ...prev, tipo_contrato: "eventual" })); }}
                      data-testid="btn-contrato-volver-lista"
                      title="Volver a la lista"
                    >×</Button>
                  </div>
                ) : (
                  <Select
                    value={formData.tipo_contrato}
                    onValueChange={(v) => {
                      if (v === "__custom__") { setContratoPersonalizado(true); setFormData(prev => ({ ...prev, tipo_contrato: "" })); }
                      else setFormData(prev => ({ ...prev, tipo_contrato: v }));
                    }}
                  >
                    <SelectTrigger className="border-stone-300" data-testid="select-contrato-personal">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {tiposContrato.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                      <SelectItem value="__custom__" data-testid="option-contrato-personalizar">+ Personalizar</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Disponibilidad</Label>
              <Select value={formData.disponibilidad} onValueChange={(v) => setFormData(prev => ({ ...prev, disponibilidad: v }))}>
                <SelectTrigger className="border-stone-300" data-testid="select-disponibilidad-personal">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {disponibilidades.map(d => (
                    <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Teléfono</Label>
                <Input value={formData.telefono} onChange={(e) => setFormData(prev => ({ ...prev, telefono: e.target.value }))} className="border-stone-300" />
              </div>
              <div className="space-y-2">
                <Label>WhatsApp</Label>
                <Input value={formData.whatsapp} onChange={(e) => setFormData(prev => ({ ...prev, whatsapp: e.target.value }))} className="border-stone-300" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Esquema de pago</Label>
              <Select value={formData.esquema_pago} onValueChange={(v) => setFormData(prev => ({ ...prev, esquema_pago: v }))}>
                <SelectTrigger className="border-stone-300" data-testid="select-esquema-pago-personal">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {esquemasPago.map(e => (
                    <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {formData.esquema_pago !== "no_definido" && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Importe ($)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.pago_monto}
                    onChange={(e) => setFormData(prev => ({ ...prev, pago_monto: e.target.value }))}
                    className="border-stone-300"
                    data-testid="input-pago-monto-personal"
                  />
                </div>
                {formData.esquema_pago === "personalizado" && (
                  <div className="space-y-2">
                    <Label>Concepto</Label>
                    <Input
                      value={formData.pago_concepto}
                      onChange={(e) => setFormData(prev => ({ ...prev, pago_concepto: e.target.value }))}
                      placeholder="Ej. Por 3 tandas, por función…"
                      className="border-stone-300"
                      data-testid="input-pago-concepto-personal"
                    />
                  </div>
                )}
              </div>
            )}
            <div className="space-y-2">
              <Label>Notas</Label>
              <Input value={formData.notas} onChange={(e) => setFormData(prev => ({ ...prev, notas: e.target.value }))} className="border-stone-300" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-stone-300">Cancelar</Button>
            <Button onClick={handleSave} className="btn-primary" data-testid="btn-guardar-personal">
              {editingPersonal ? "Actualizar" : "Agregar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar personal?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Personal;
